import { BookingStatus, ShiftRepeat } from "@repo/database";
import { groupBy, isBetween } from "@repo/utils";
import {
  addMinutes,
  differenceInMinutes,
  format,
  isSameDay,
  isSameMinute,
  max,
  min,
  set,
  subMinutes,
} from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { useTranslation } from "react-i18next";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";

import type { ChartConfig } from "~/components/ui/chart";
import type { Booking } from "~/providers/booking-provider";
import type { Shift } from "~/providers/shift-provider";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "~/components/ui/chart";
import { Label } from "~/components/ui/label";
import { useUser } from "~/hooks/use-user";
import { api } from "~/lib/api";

const calculateIncrement = (maxGuestsRounded: number) => {
  if (maxGuestsRounded <= 40) {
    return 10;
  } else if (maxGuestsRounded <= 100) {
    return 20;
  } else if (maxGuestsRounded <= 200) {
    return 50;
  } else {
    return 100; // Default increment for larger numbers
  }
};

const generateTimeslots = (shifts: Shift[], selectedDate: Date) => {
  const finalShifts: Shift[] = [];

  const nonRepeatingShifts = shifts.filter(
    (shift) =>
      shift.repeat === ShiftRepeat.never &&
      isSameDay(shift.startTime, selectedDate),
  );

  if (nonRepeatingShifts.length > 0) {
    finalShifts.push(...nonRepeatingShifts);
  } else {
    finalShifts.push(...shifts);
  }

  const interval = finalShifts.reduce(
    (acc, curr) => Math.max(acc, curr.timeslotInterval),
    0,
  );

  const allStartTimes = finalShifts.map((shift) =>
    subMinutes(
      set(selectedDate, {
        hours: shift.startTime.getHours(),
        minutes: shift.startTime.getMinutes(),
        seconds: 0,
      }),
      shift.standardDuration,
    ),
  );

  const allEndTimes = finalShifts.map((shift) =>
    addMinutes(
      set(selectedDate, {
        hours: shift.endTime.getHours(),
        minutes: shift.endTime.getMinutes(),
        seconds: 0,
      }),
      shift.standardDuration,
    ),
  );

  const startOfDay = min(allStartTimes);

  const endOfDay = max(allEndTimes);

  const diff = differenceInMinutes(endOfDay, startOfDay);

  const timeslots: { time: Date; capacity: number; maxGuests: number }[] = [];

  for (let i = 0; i <= diff; i += interval) {
    const timeslotTime = addMinutes(startOfDay, i);
    timeslots.push({
      time: timeslotTime,
      capacity: 0,
      maxGuests: 0,
    });
  }

  for (const shift of finalShifts) {
    for (const shiftTimeslot of shift.timeslots) {
      const shiftSlotTime = set(selectedDate, {
        hours: shiftTimeslot.start.getHours(),
        minutes: shiftTimeslot.start.getMinutes(),
        seconds: 0,
      });

      const timeslot = timeslots.find(
        (t) => t.time.getTime() === shiftSlotTime.getTime(),
      );

      if (timeslot) {
        if (shiftTimeslot.temporary) {
          if (!isSameDay(shiftTimeslot.start, selectedDate)) {
            continue;
          } else {
            if (shiftTimeslot.closed) {
              timeslot.maxGuests = 0;
            } else {
              timeslot.maxGuests = shiftTimeslot.spots;
            }
            timeslot.capacity = shift.capacity;
            continue;
          }
        }
        timeslot.capacity = shift.capacity;

        timeslot.maxGuests += shiftTimeslot.spots;
      }
    }

    for (const timeslot of timeslots) {
      timeslot.capacity = shift.capacity;
    }
  }

  // Update timeslots based on shifts
  finalShifts.forEach((shift) => {
    // Ensure all timeslots within the shift period have the correct capacity
  });

  return timeslots;
};

export function OccupationFlowChart({
  selectedDate,
  bookings,
  shifts,
}: {
  selectedDate: Date;
  bookings: Booking[];
  shifts: Shift[];
}) {
  const { t } = useTranslation("common");
  const { user } = useUser();
  const { data: dayData } = api.shift.getDay.useQuery(selectedDate);
  // const timeslots = generateTimeslots(
  //   shifts.filter((shift) => shift.closed === false),
  //   selectedDate,
  // );
  const formattedShifts = dayData?.map((d) => ({
    ...d,
    timeslots: d.timeslots.map((t) => {
      const start = toZonedTime(t.start, user.timeZone);
      return {
        ...t,
        start: set(selectedDate, {
          hours: start.getHours(),
          minutes: start.getMinutes(),
          seconds: 0,
          milliseconds: 0,
        }),
      };
    }),
  }));

  const timeslots = formattedShifts?.flatMap((shift) => shift.timeslots);

  const data = timeslots
    ?.sort((a, b) => {
      const timeA =
        a.start.getHours() * 3600000 +
        a.start.getMinutes() * 60000 +
        a.start.getSeconds() * 1000 +
        a.start.getMilliseconds();
      const timeB =
        b.start.getHours() * 3600000 +
        b.start.getMinutes() * 60000 +
        b.start.getSeconds() * 1000 +
        b.start.getMilliseconds();
      return timeA - timeB;
    })
    .map((slot) => {
      let activeCount = 0;
      let checkedInCount = 0;

      // Iterate through bookings for the selected date
      bookings.forEach((booking) => {
        const endTime = subMinutes(booking.endTime, 1);
        if (
          isSameDay(booking.startTime, selectedDate) &&
          isSameMinute(booking.startTime, slot.start)
        ) {
          activeCount += booking.groupSize;
          checkedInCount += booking.groupSize;
        } else if (
          isSameDay(booking.startTime, selectedDate) &&
          !isSameMinute(booking.startTime, slot.start) &&
          isBetween(slot.start, booking.startTime, endTime)
        ) {
          checkedInCount += booking.groupSize;
        }
      });

      return {
        time: format(slot.start, "p"),
        checked_in: checkedInCount,
        active: activeCount,
        capacity: slot.closed ? 0 : slot.capacity,
        maxGuests: slot.closed ? 0 : slot.spots,
      };
    });

  const peakData = data?.reduce(
    (acc, curr) => {
      if (curr.checked_in > acc.checkedIn) {
        return { checkedIn: curr.checked_in, time: curr.time };
      }
      return acc;
    },
    { checkedIn: 0, time: "" },
  );

  const peakMoment = peakData?.checkedIn ?? 0;

  const peakTime = peakData?.time ?? "";

  const capacity = Math.max(...(timeslots?.map((t) => t.capacity) ?? []), 0);

  const effectiveCapacity = peakMoment > capacity + 10 ? peakMoment : capacity;

  const capacityRounded = Math.ceil((effectiveCapacity + 10) / 10) * 10;

  const guestsToday = bookings.reduce(
    (totalGuests, booking) => totalGuests + booking.groupSize,
    0,
  );

  const averageGroupSize =
    guestsToday > 0 ? Math.round((guestsToday / bookings.length) * 2) / 2 : 0;

  const increment = calculateIncrement(capacityRounded);

  const tickCount = Math.floor(capacityRounded / increment) + 1;

  const ticks = Array.from({ length: tickCount }, (_, i) => i * increment);

  const occupationChartConfig = {
    checked_in: {
      label: t("Booking.seated_guests"),
      color: "hsl(var(--chart-1))",
    },
    active: {
      label: t("Booking.incoming_guests"),
      color: "hsl(var(--chart-2))",
    },
    capacity: {
      label: t("Booking.capacity"),
      color: "#000000",
    },
    maxGuests: {
      label: t("Booking.max_guests"),
      color: "#ececec",
    },
  } satisfies ChartConfig;

  const isValidSource = (key: string): key is keyof typeof sourceChartConfig =>
    key in sourceChartConfig;

  const sourceChartConfig = {
    guests: {
      label: "Guests",
      color: "#cccccc",
    },
    google_reserve: {
      label: "Google",
      color: "#ecb615",
    },
    widget: {
      label: t("Booking.source.widget"),
      color: "hsl(var(--brand))",
    },
    social_deal: {
      label: "Social deal",
      color: "#00bff6",
    },
    undefined: {
      label: t("Booking.source.manual"),
      color: "#cccccc",
    },
  } satisfies ChartConfig;

  const bookingsBySource = groupBy(bookings, "provider");

  const sourceColorMappings = {
    google_reserve: "#ecb615",
    social_deal: "#00bff6",
    widget: "hsl(var(--brand))",
  };

  const sourceData = Object.entries(bookingsBySource).map(
    ([source, bookings]) => ({
      source,
      guests: bookings.reduce((sum, booking) => sum + booking.groupSize, 0),
      fill:
        sourceColorMappings[source as keyof typeof sourceColorMappings] ||
        "#cccccc",
    }),
  );

  return (
    <div>
      <div className="flex flex-wrap">
        <div className="flex flex-col gap-2 p-4 w-full md:w-1/2 lg:w-1/3 border-b md:border-r bg-card">
          <Label size="lg">{t("Booking.guests_and_reservations")}</Label>
          <span className="text-2xl font-medium">{`${guestsToday} / ${bookings.length}`}</span>
        </div>
        <div className="flex flex-col gap-2 p-4 w-full md:w-1/2 lg:w-1/3 border-b md:border-r bg-card">
          <Label size="lg">{t("Booking.average_group_size")}</Label>
          <span className="text-2xl font-medium">{averageGroupSize}</span>
        </div>
        <div className="flex flex-col gap-2 p-4 w-full md:w-1/2 lg:w-1/3 border-b md:border-r lg:border-r-0 bg-card">
          <Label size="lg">{t("Booking.peak_moment")}</Label>
          {peakTime && (
            <span className="text-2xl font-medium">{`${peakTime} / ${peakMoment}`}</span>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2 bg-card p-4 overflow-auto small-scrollbar">
        <Label size="lg">{t("Booking.guests_per_time")}</Label>
        <ChartContainer
          className="min-h-[200px] max-h-[400px] min-w-[850px] w-full"
          config={occupationChartConfig}
        >
          <ComposedChart accessibilityLayer data={data}>
            <CartesianGrid
              stroke="currentColor"
              strokeWidth={0.5}
              className="stroke-neutral-300 dark:stroke-neutral-600"
            />

            {ticks.map((t, index) => (
              <ReferenceLine
                key={`y-line-${index}`}
                yAxisId="guests"
                xAxisId="one"
                y={t}
                stroke="#808080"
                strokeWidth={1}
              />
            ))}

            <ChartTooltip
              content={({ payload, label }) => {
                // Ensure payload has data
                if (!payload || payload.length === 0) {
                  return null;
                }

                return (
                  <div className="w-[200px] bg-card p-4 flex flex-col gap-4 border rounded">
                    {/* Show the label only once */}
                    <span className="font-bold text-[18px] text-muted-foreground">
                      {label}
                    </span>

                    {/* Iterate through payload to display individual items */}
                    {payload.map((item, index) => (
                      <div
                        key={index}
                        className="flex flex-row items-center gap-2"
                      >
                        <div
                          className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                          style={{
                            background: item.color,
                          }}
                        />
                        <span className="text-[15px]">{`${item.name}: ${item.value?.toString()}`}</span>
                      </div>
                    ))}
                  </div>
                );
              }}
              cursor={false}
            />
            {data
              ?.filter((d) => {
                const [_hour, minute] = d.time.split(":");
                return minute === "00"; // full hour
              })
              .map((d) => (
                <ReferenceLine
                  strokeWidth={0.75}
                  xAxisId="one"
                  yAxisId="guests"
                  key={d.time}
                  x={d.time}
                  stroke="#808080" // Different color for full-hour lines
                />
              ))}
            <ChartLegend content={<ChartLegendContent />} />
            <Bar
              xAxisId="one"
              yAxisId="guests"
              dataKey={BookingStatus.checked_in}
              fill="#92b1f5"
              name={t("Booking.seated_guests")}
            />
            <YAxis
              yAxisId="guests"
              domain={[0, capacityRounded]}
              ticks={ticks} // Use the dynamically generated ticks
            />

            <XAxis
              dataKey="time"
              xAxisId="one"
              axisLine={false}
              tickLine={false}
              interval={0}
              tickFormatter={(time) => {
                const [_hour, minute] = time.split(":"); // Split time into hour and minute
                return minute === "00" ? time : ""; // Show hour only if minutes are "00"
              }}
            />
            <Bar
              yAxisId="guests"
              xAxisId="two"
              dataKey={BookingStatus.active}
              name={t("Booking.incoming_guests")}
              fill="#2563eb"
            />
            <XAxis xAxisId="two" hide />
            <Line
              dataKey="capacity" // Replace with your desired key
              xAxisId="one"
              yAxisId="guests"
              type="monotone" // Smooth curve
              dot={false}
              stroke="#FF0000" // Line color (e.g., amber)
              strokeWidth={2} // Line thickness
              name={t("Booking.capacity")}
            />

            <Area
              xAxisId="one"
              yAxisId="guests"
              dataKey="maxGuests"
              type="step"
              fill="rgba(238, 238, 238, 0.28)"
              fillOpacity={0.4}
              stroke="#ececec"
              strokeDasharray={6}
              name={t("Booking.max_guests")}
            />
          </ComposedChart>
        </ChartContainer>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 grid-cols-1 border-t">
        <div className="gap-2 bg-card p-4 overflow-auto md:border-r">
          <Label size="lg">{t("Booking.guests_per_source")}</Label>
          <ChartContainer
            className="min-h-[200px] max-h-[400px] w-full"
            config={sourceChartConfig}
          >
            <PieChart>
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent hideLabel />}
              />
              <Pie data={sourceData} dataKey="guests" nameKey="source" />
              <ChartLegend
                content={<ChartLegendContent nameKey="source" />}
                className="-translate-y-2 flex-wrap gap-2 [&>*]:basis-1/4 [&>*]:justify-center"
              />
            </PieChart>
          </ChartContainer>
        </div>
        <div className="gap-4 flex flex-col bg-card p-4 overflow-auto md:border-r border-t md:border-t-0">
          <Label size="lg">{t("Booking.guests_per_source_percentage")}</Label>
          <div className="flex flex-col gap-2">
            {[...sourceData]
              .sort((a, b) => b.guests / guestsToday - a.guests / guestsToday) // Sort by percentage, descending
              .map(({ source, guests }) => {
                const percentage = Math.round((guests / guestsToday) * 100);
                return (
                  <div key={source} className="flex flex-row gap-2">
                    <div
                      className="font-medium"
                      style={{
                        color: isValidSource(source)
                          ? sourceChartConfig[source].color
                          : "",
                      }}
                    >
                      {isValidSource(source)
                        ? sourceChartConfig[source].label
                        : ""}
                    </div>
                    <div className="border-b flex-1 border-dashed mb-1"></div>
                    <div>{`${percentage}%`}</div>
                  </div>
                );
              })}
          </div>
        </div>
        <div className="gap-4 flex flex-col bg-card p-4 overflow-auto md:border-r border-t md:border-t-0">
          <Label size="lg">{t("Booking.new_vs_returning_guests")}</Label>
          <div className="flex flex-col gap-2">
            <div className="flex flex-row gap-2">
              <div className="font-medium">{t("Booking.new_guests")}</div>
              <div className="border-b flex-1 border-dashed mb-1"></div>
              <div>{`...%`}</div>
            </div>
            <div className="flex flex-row gap-2">
              <div className="font-medium">{t("Booking.returning_guests")}</div>
              <div className="border-b flex-1 border-dashed mb-1"></div>
              <div>{`...%`}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
